import React, { useState, useRef, useEffect } from "react";
import {
  Flex,
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  Link,
  PinInput,
  PinInputField,
} from "@chakra-ui/react";
import { EmailIcon, ViewIcon, ViewOffIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { FaUserShield, FaChartLine, FaRocket } from "react-icons/fa";
import { useAuth } from "../hooks/useAuth";
import { showToast } from "../utils/toastUtils";

// ─── Animation ────────────────────────────────────────────────────────────────
// Inject step-transition keyframe once into <head>
if (typeof document !== "undefined" && !document.getElementById("zen-step-anim")) {
  const s = document.createElement("style");
  s.id = "zen-step-anim";
  s.textContent = `@keyframes stepIn { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }`;
  document.head.appendChild(s);
}
const stepAnimStyle: React.CSSProperties = { animation: "stepIn 0.22s ease both" };

// ─── Constants ────────────────────────────────────────────────────────────────
const BACKEND_URL = "https://api-xk2q5lbbua-uc.a.run.app";
const FIREBASE_API_KEY = "AIzaSyCL-5ffVmfDAQ8fMkmG7cqR95wsRemB3gs";
const FIREBASE_SIGNIN_URL = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${FIREBASE_API_KEY}`;


type Step = "credentials" | "otp";

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Returns localId + idToken via REST — does NOT touch SDK auth state
const validateCredentials = async (
  email: string,
  password: string
): Promise<{ localId: string; idToken: string }> => {
  const res = await fetch(FIREBASE_SIGNIN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, returnSecureToken: true }),
  });
  if (!res.ok) {
    const data = await res.json();
    const code: string = data?.error?.message || "";
    if (code.includes("INVALID_PASSWORD") || code.includes("EMAIL_NOT_FOUND") || code.includes("INVALID_LOGIN_CREDENTIALS"))
      throw new Error("Invalid email or password. Please try again.");
    if (code.includes("TOO_MANY_ATTEMPTS_TRY_LATER") || code.includes("USER_DISABLED"))
      throw new Error("Account temporarily locked. Please try again later.");
    throw new Error("Login failed. Please check your credentials.");
  }
  const data = await res.json();
  return { localId: data.localId, idToken: data.idToken };
};

// Checks isMfaEnabled on the user's org doc via Firestore REST (no SDK auth state)
const FIRESTORE_BASE =
  `https://firestore.googleapis.com/v1/projects/zen-sight/databases/(default)/documents`;

const checkOrgMfa = async (localId: string, idToken: string): Promise<boolean> => {
  try {
    // 1. Get users/{localId} to find organizationId
    const userRes = await fetch(`${FIRESTORE_BASE}/users/${localId}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!userRes.ok) return true; // default: MFA on
    const userDoc = await userRes.json();
    const orgId: string | undefined = userDoc.fields?.organizationId?.stringValue;
    if (!orgId) return true;

    // 2. Get organization/{orgId} to check isMfaEnabled
    const orgRes = await fetch(`${FIRESTORE_BASE}/organization/${orgId}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!orgRes.ok) return true;
    const orgDoc = await orgRes.json();
    const isMfaEnabled = orgDoc.fields?.isMfaEnabled?.booleanValue;
    // If field absent or true → MFA is on; only skip if explicitly false
    return isMfaEnabled !== false;
  } catch {
    return true; // safe default: MFA on
  }
};

const sendOtpApi = async (email: string): Promise<void> => {
  const res = await fetch(`${BACKEND_URL}/api/email/send-login-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to send verification code.");
  }
};

const verifyOtpApi = async (email: string, otp: string): Promise<void> => {
  const res = await fetch(`${BACKEND_URL}/api/email/verify-login-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, otp }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "OTP verification failed.");
  }
};

// ─── Component ────────────────────────────────────────────────────────────────
const Login: React.FC = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  // Step state
  const [step, setStep] = useState<Step>("credentials");

  // Credentials step
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // OTP step
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const startResendCooldown = () => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // ── Step 1: Sign In ───────────────────────────────────────────────────────
  const handleLogin = async () => {
    if (!email || !password) {
      showToast({ title: "Error", description: "Please enter both email and password.", status: "error", duration: 3000, isClosable: true });
      return;
    }
    try {
      setLoading(true);

      // Super Admin bypass
      const superEmail = (process.env.REACT_APP_SUPER_ADMIN_EMAIL || "").trim().toLowerCase();
      const superPass  = process.env.REACT_APP_SUPER_ADMIN_PASS || "";
      if (superEmail && email.trim().toLowerCase() === superEmail) {
        if (superPass && password === superPass) {
          localStorage.setItem("isSuperAdmin", "true");
          window.dispatchEvent(new Event("superAdminStatusChanged"));
          navigate("/superadmin", { replace: true });
          return;
        }
        throw new Error("Invalid super admin credentials.");
      }

      // 1️⃣ Validate via Firebase REST (no SDK state change) — also get localId + idToken
      const { localId, idToken } = await validateCredentials(email, password);

      // 2️⃣ Check if MFA is enabled for this org
      const mfaEnabled = await checkOrgMfa(localId, idToken);

      if (!mfaEnabled) {
        // MFA disabled — sign in directly without OTP
        setLoading(false);
        await signIn(email, password, (path: string) => navigate(path, { replace: true }));
        showToast({ title: "Login Successful", description: "Welcome back!", status: "success", duration: 3000, isClosable: true });
        return;
      }

      // 3️⃣ MFA enabled — send OTP
      await sendOtpApi(email);
      startResendCooldown();

      setOtp("");
      setStep("otp");
    } catch (err: any) {
      showToast({ title: "Login Failed", description: err.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP ────────────────────────────────────────────────────
  // Takes an explicit value so onComplete can pass the completed pin directly,
  // avoiding the React async state-update race condition.
  const handleVerifyOtp = async (otpValue?: string) => {
    const code = (otpValue ?? otp).trim();
    if (code.length !== 6) {
      showToast({ title: "Incomplete Code", description: "Please enter all 6 digits.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    try {
      setOtpLoading(true);

      // 3️⃣ Verify OTP
      await verifyOtpApi(email, code);

      // 4️⃣ Real sign-in via useAuth
      await signIn(email, password, (path: string) => navigate(path, { replace: true }));

      showToast({ title: "Login Successful", description: "Welcome back!", status: "success", duration: 3000, isClosable: true });
    } catch (err: any) {
      showToast({ title: "Verification Failed", description: err.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      await sendOtpApi(email);
      startResendCooldown();
      setOtp("");
      showToast({ title: "Code Resent", description: "A new verification code has been sent.", status: "info", duration: 3000, isClosable: true });
    } catch (err: any) {
      showToast({ title: "Error", description: err.message, status: "error", duration: 3000, isClosable: true });
    }
  };

  // ── Back to credentials ───────────────────────────────────────────────────
  const handleBack = () => {
    setStep("credentials");
    setOtp("");
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setResendCooldown(0);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Flex height="100vh" width="100vw" bgGradient="linear(to-br,rgb(163, 65, 243), #4B00FF)" alignItems="stretch" justifyContent="stretch">
      <Flex flex={1} alignItems="stretch" justifyContent="stretch">

        {/* LEFT PANEL */}
        <Box flex={1} display={{ base: "none", md: "flex" }} flexDirection="column" color="white" bg="#0B0B0F" alignItems="stretch" justifyContent="space-between">
          <Box pt={10} px={8}>
            <VStack spacing={6} textAlign="center">
              <Text fontSize="4xl" fontWeight="bold" bgGradient="linear(to-r, #FBD282, #B59143)" bgClip="text" letterSpacing="wider">
                Welcome to ZenSight!
              </Text>
              <Text fontSize="lg" opacity={0.85} color="rgba(255,255,255,0.85)" fontWeight="medium">
                Your personalized dashboard is ready. Log in to access exclusive features and manage your data effortlessly.
              </Text>
            </VStack>
          </Box>
          <Box px={8} pt={8} mb={20} pb={8}>
            <VStack spacing={8} align="center">
              {[
                { Icon: FaUserShield, label: "Secure Access",      desc: "Your data is protected with bank-grade encryption and multi-factor login." },
                { Icon: FaChartLine,  label: "Track Progress",     desc: "Monitor skill gains and performance metrics in real time." },
                { Icon: FaRocket,     label: "Boost Performance",  desc: "Leverage insights to accelerate your team's growth and productivity." },
              ].map(({ Icon, label, desc }) => (
                <VStack key={label} spacing={2}>
                  <Box>{(Icon as any)({ size: 24, color: "#FBD282" })}</Box>
                  <Text fontSize="lg" fontWeight="semibold">{label}</Text>
                  <Text fontSize="sm" opacity={0.8} textAlign="center" maxW="300px">{desc}</Text>
                </VStack>
              ))}
            </VStack>
          </Box>
        </Box>

        {/* RIGHT PANEL */}
        <Box flex={{ base: 1, md: 1 }} bg="white" display="flex" flexDirection="column" justifyContent="center"
          px={{ base: 4, md: 8 }} py={{ base: 6, md: 8 }} position="relative" overflow="hidden">
          <Box position="absolute" top="-100px" right="-100px" w="300px" h="300px" borderRadius="full" bg="rgba(26,28,31,0.06)" zIndex="0" display={{ base: "none", md: "block" }} />
          <Box position="absolute" bottom="-50px" left="-50px" w="200px" h="200px" borderRadius="full" bg="rgba(58,69,83,0.08)" zIndex="0" display={{ base: "none", md: "block" }} />

          <Box position="relative" zIndex="1" bg="white" borderRadius={{ base: "none", md: "2xl" }}
            boxShadow={{ base: "none", md: "0 4px 20px rgba(0,0,0,0.08)" }}
            p={{ base: 4, md: 8 }} maxW="500px" mx="auto" w="100%">

            {/* Title — always visible */}
            <Box textAlign="center" mb={8}>
              <Heading as="h2" size="2xl">
                <Text as="span" fontFamily="Roboto, sans-serif" fontSize="5xl" fontWeight="700" bgGradient="linear(45deg, #FBD282, #B59143)" bgClip="text" display="inline">Zen</Text>
                <Text as="span" fontFamily="Roboto, sans-serif" fontSize="5xl" fontWeight="700" color="black">Sight</Text>
              </Heading>
              <Text mt={2} color="#4A5463" fontSize="md">
                {step === "credentials" ? "Your Gateway to Intelligent Insights" : "Two-Factor Verification"}
              </Text>
            </Box>

            {/* ── STEP 1: Credentials ───────────────────────────────────── */}
            {step === "credentials" && (
              <Box style={stepAnimStyle}>
                <Box w="100%">
                  <FormControl isRequired mb={6}>
                    <FormLabel fontSize="sm" color="#B59143" fontWeight="medium">Email Address</FormLabel>
                    <InputGroup>
                      <Input
                        id="login-email" fontSize="md" type="email" placeholder="Enter your email"
                        value={email} textColor="gray.700" onChange={(e) => setEmail(e.target.value)}
                        borderRadius="lg" border="1px solid" borderColor="#2E2F33"
                        _focus={{ borderColor: "#B59143", boxShadow: "0px 0px 12px rgba(181,145,67,0.45)" }}
                        _hover={{ borderColor: "#B59143" }}
                      />
                      <InputRightElement color="#B59143"><EmailIcon /></InputRightElement>
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired mb={8}>
                    <FormLabel fontSize="sm" color="#B59143" fontWeight="medium">Password</FormLabel>
                    <InputGroup>
                      <Input
                        id="login-password" fontSize="md" type={showPassword ? "text" : "password"}
                        placeholder="Enter your password" textColor="gray.700"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleLogin(); }}
                        borderRadius="lg" border="1px solid" borderColor="#2E2F33"
                        _focus={{ borderColor: "#B59143", boxShadow: "0px 0px 12px rgba(181,145,67,0.45)" }}
                        _hover={{ borderColor: "#B59143" }}
                      />
                      <InputRightElement>
                        <IconButton size="sm" variant="ghost" color="#B59143"
                          icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>

                  <Button
                    id="login-sign-in-btn" fontSize="md"
                    bgGradient="linear(to-r, #FBD282, #B59143)"
                    color="white" fontWeight="bold" borderRadius="lg" w="100%" h="50px"
                    isLoading={loading} onClick={handleLogin} mb={6}
                    _hover={{ bgGradient: "linear(to-r, #E8C16C, #A7843D)", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(181,145,67,0.35)" }}
                    _active={{ transform: "translateY(0)" }} transition="all 0.2s"
                  >
                    Sign In
                  </Button>

                  <Text textAlign="center" mb={8}>
                    <Link as={RouterLink} to="/forgot-password" fontSize="sm" color="#B59143" fontWeight="medium"
                      _hover={{ textDecoration: "none", color: "#A7843D" }} transition="all 0.2s">
                      Forgot your password?
                    </Link>
                  </Text>

                  <HStack spacing={6} justify="center">
                    <Link fontSize="sm" color="#4A5463" _hover={{ textDecoration: "none", color: "#A7843D" }} transition="all 0.2s">Terms of Use</Link>
                    <Text color="gray.300">|</Text>
                    <Link fontSize="sm" color="#4A5463" _hover={{ textDecoration: "none", color: "#A7843D" }} transition="all 0.2s">Privacy Policy</Link>
                  </HStack>
                </Box>
              </Box>
            )}

            {/* ── STEP 2: OTP Entry ─────────────────────────────────────── */}
            {step === "otp" && (
              <Box style={stepAnimStyle}>
                <Box w="100%">

                  {/* Sent-to banner */}
                  <Box
                    bg="linear-gradient(135deg, #fffbf0 0%, #fef9e7 100%)"
                    border="1.5px solid #f4d03f"
                    borderRadius="xl"
                    px={5} py={4} mb={7}
                  >
                    <Text fontSize="sm" color="#b8860b" fontWeight="600" mb={1}>
                      Verification code sent to:
                    </Text>
                    <Text fontSize="sm" color="#101828" fontWeight="700">{email}</Text>
                    <Text fontSize="xs" color="#667085" mt={1}>
                      Enter the 6-digit code below. It expires in <strong>10 minutes</strong>.
                    </Text>
                  </Box>

                  {/* PIN input */}
                  <FormControl mb={7}>
                    <HStack spacing={3} justify="center">
                      <PinInput
                        value={otp}
                        onChange={setOtp}
                        otp
                        size="lg"
                        focusBorderColor="#B59143"
                      >
                        {[...Array(6)].map((_, i) => (
                          <PinInputField
                            key={i}
                            borderColor="gray.300"
                            fontWeight="700"
                            fontSize="xl"
                            borderRadius="lg"
                            _focus={{ borderColor: "#B59143", boxShadow: "0 0 0 2px rgba(181,145,67,0.35)" }}
                          />
                        ))}
                      </PinInput>
                    </HStack>
                  </FormControl>

                  {/* Verify button */}
                  <Button
                    id="login-verify-otp-btn" fontSize="md"
                    bgGradient="linear(to-r, #FBD282, #B59143)"
                    color="white" fontWeight="bold" borderRadius="lg" w="100%" h="50px"
                    isLoading={otpLoading}
                    onClick={() => handleVerifyOtp()}
                    mb={5}
                    _hover={{ bgGradient: "linear(to-r, #E8C16C, #A7843D)", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(181,145,67,0.35)" }}
                    _active={{ transform: "translateY(0)" }} transition="all 0.2s"
                  >
                    Verify & Login
                  </Button>

                  {/* Resend + Back */}
                  <VStack spacing={3}>
                    <HStack spacing={1} fontSize="sm">
                      <Text color="gray.500">Didn't receive the code?</Text>
                      <Button
                        variant="link" fontSize="sm"
                        color={resendCooldown > 0 ? "gray.400" : "#B59143"}
                        fontWeight="600" isDisabled={resendCooldown > 0}
                        onClick={handleResendOtp}
                        _hover={{ textDecoration: "none", color: "#A7843D" }}
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Code"}
                      </Button>
                    </HStack>

                    <Button
                      variant="ghost" size="sm" color="gray.500"
                      leftIcon={<ArrowBackIcon />}
                      onClick={handleBack}
                      _hover={{ color: "#B59143", bg: "transparent" }}
                    >
                      Back to Sign In
                    </Button>
                  </VStack>
                </Box>
              </Box>
            )}

          </Box>
        </Box>
      </Flex>
    </Flex>
  );
};

export default Login;
